import { atom } from 'nanostores';

/**
 * Public feature flags the signup funnel depends on. These resolve for
 * anonymous visitors through the unauthenticated `public_feature_flags`
 * endpoint — account feature flags can't apply here, because signup runs
 * pre-auth, before any account exists.
 *
 * This module deliberately depends only on nanostores so `route-ownership.ts`
 * (a very widely imported module) can read the store without pulling the API
 * client into its import graph.
 */
export const SignupPublicFeatureFlag = {
  /**
   * Rollout flag for the `/signup` migration: on, Hydra serves the funnel;
   * off (the default), the embedded app does.
   */
  SignupMigration: 'signup-migration',
  /** Renders the funnel in the dark theme. */
  SignupDarkMode: 'signup_dark_mode',
  /**
   * Enables the startup-program signup variant (`?signupType=startup`).
   * When unresolved or off, a startup signup downgrades to `journeys`,
   * matching the embedded app.
   */
  SignupStartupForm: 'signup_startup_form',
} as const;

export type SignupPublicFeatureFlag = (typeof SignupPublicFeatureFlag)[keyof typeof SignupPublicFeatureFlag];

/**
 * `null` means the public-flags fetch hasn't settled yet; an object means it
 * settled (a failed fetch settles to `{}`, i.e. every flag off).
 */
export type SignupPublicFlags = Record<string, boolean> | null;

export const signupPublicFlagsStore = atom<SignupPublicFlags>(null);

export function hasSignupPublicFlag(flags: SignupPublicFlags, flag: SignupPublicFeatureFlag): boolean {
  return flags?.[flag] === true;
}
